/**
 * A report on letter pages through the print root: a title page's worth of header on the first
 * page, then each section, forty rows to a page so the page-count proof can count what it
 * expects. The rows are the engine's rows; nothing is computed here.
 */
import type { ReactNode } from 'react';
import { Paper, PrintRoot, formatMoney } from '@cider/ui';
import {
  engineSays,
  useLoad,
  type Cell,
  type Report,
  type ReportSection,
} from '../views/money/shared.js';

const ROWS_PER_PAGE = 40;

const show = (s: ReportSection, c: Cell, i: number): string =>
  c === null ? '' : typeof c === 'number' && s.money.includes(i) ? formatMoney(c) : String(c);

/** The pages: [section, rows on this page, first page of the section]. */
export function paginate(
  r: Report,
): { section: ReportSection; rows: Cell[][]; first: boolean; totals: boolean }[] {
  const pages: { section: ReportSection; rows: Cell[][]; first: boolean; totals: boolean }[] = [];
  for (const s of r.sections) {
    const all = [...s.rows];
    if (all.length === 0) {
      pages.push({ section: s, rows: [], first: true, totals: !!s.totals });
      continue;
    }
    for (let i = 0; i < all.length; i += ROWS_PER_PAGE) {
      const last = i + ROWS_PER_PAGE >= all.length;
      pages.push({
        section: s,
        rows: all.slice(i, i + ROWS_PER_PAGE),
        first: i === 0,
        totals: last && !!s.totals,
      });
    }
  }
  return pages;
}

export function ReportPages({ kind }: { kind: string }): ReactNode {
  const q = window.location.search.replace(/^\?/, '');
  const report = useLoad<Report>(`/money/reports/${kind}${q ? `?${q}` : ''}`);
  if (report.error) return <p role="alert">{report.error}</p>;
  const r = report.data && 'sections' in report.data ? report.data : null;
  if (!r) return <p>Reading…</p>;
  const pages = r.status === 'NO DATA' || r.sections.length === 0 ? [null] : paginate(r);
  return (
    <PrintRoot
      kind="letter"
      title={r.title}
      pages={pages.map((p, i) => (
        <Paper
          key={i}
          masthead={r.title}
          submast={`Saint Lawrence Apple Fest · as of ${r.asOf} · read through ${engineSays(r.engine)}`}
          stamp={`PAGE ${String(i + 1)} OF ${String(pages.length)}`}
        >
          <div className="report">
            {p === null ? (
              <p>{r.says}</p>
            ) : (
              <>
                <h2>
                  {p.section.head}
                  {p.first ? '' : ' (continued)'}
                </h2>
                <table>
                  <thead>
                    <tr>
                      {p.section.columns.map((c, ci) => (
                        <th key={ci} className={p.section.money.includes(ci) ? 'num' : undefined}>
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {p.rows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((c, ci) => (
                          <td key={ci} className={p.section.money.includes(ci) ? 'num' : undefined}>
                            {show(p.section, c, ci)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {p.totals && p.section.totals ? (
                      <tr className="total">
                        {p.section.totals.map((c, ci) => (
                          <td key={ci} className={p.section.money.includes(ci) ? 'num' : undefined}>
                            {show(p.section, c, ci)}
                          </td>
                        ))}
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </Paper>
      ))}
    />
  );
}
